// Data Lab v2 · Suppliers — KPI band + category facets + top-rebate leaderboard.
import { useEffect, useMemo, useState } from "react";
import Layout from "../../../components/Layout";
import api from "../../../lib/api";
import LabListTable from "../LabListTable";
import { Chip, asText } from "../_shared";
import { KpiBand, KpiTile, FacetChips, V2Banner, disableV2 } from "./_shared";
import { T2Panel } from "../../quotes/_t2";
import { Link } from "react-router-dom";
import { Factory, Percent, Trophy, ToggleRight } from "lucide-react";

const PCT = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  // Suppliers stored as decimals 0.05 = 5% OR ints. Normalise.
  const shown = n <= 1 ? n * 100 : n;
  return `${shown.toFixed(2)}%`;
};

const COLUMNS = [
  { key: "supplier",  label: "Supplier",        path: "SupplierName",       sortable: true, w: "w-64", className: "font-semibold text-white",
    fmt: (v, row) => v || row.name || "—" },
  { key: "account",   label: "Account #",       path: "AccountNumber",      sortable: true, w: "w-28", className: "font-mono text-[#C8A46B] text-xs" },
  { key: "category",  label: "Category",        path: "Category",
    fmt: (v, row) => {
      const val = v || row.category;
      return val ? <Chip tone="slate">{val}</Chip> : <span className="text-slate-500">—</span>;
    }, w: "w-32" },
  { key: "contact",   label: "Primary contact", path: "ContactName1",
    fmt: (v, row) => (
      <div className="min-w-0">
        <div className="text-sm text-slate-100">{asText(v)}</div>
        {row.ContactName1Email && <div className="font-mono text-[11px] text-[#C8A46B] truncate">{row.ContactName1Email}</div>}
      </div>
    ) },
  { key: "location",  label: "Location",        path: "City", sortable: true, sortPath: "City",
    fmt: (v, row) => (
      <div className="min-w-0">
        <div className="text-sm text-slate-100">{asText(v) === "—" ? "—" : v}{row.County ? `, ${row.County}` : ""}</div>
        {row.Postcode && <div className="font-mono text-[11px] text-slate-400">{row.Postcode}</div>}
      </div>
    ) },
  { key: "rebate",    label: "Rebate",          path: "PercentRebate",   sortable: true, w: "w-24", fmt: PCT, className: "font-mono text-teal-300" },
  { key: "discount",  label: "Discount",        path: "PercentDiscount", sortable: true, w: "w-24", fmt: PCT, className: "font-mono text-amber-300" },
  { key: "enabled",   label: "Enabled",         path: "Enabled",         sortable: true, w: "w-24",
    fmt: (v) => v === true ? <Chip tone="green">Live</Chip> : v === false ? <Chip tone="red">Off</Chip> : "—" },
];

export default function LabSuppliersV2() {
  const [insights, setInsights] = useState(null);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/lab/suppliers/insights"); setInsights(data); }
      catch { setInsights(null); }
    })();
  }, []);

  const searchAppend = useMemo(() => cats.join(" "), [cats]);

  return (
    <Layout>
      <div data-testid="lab-suppliers-v2">
        <V2Banner onDisable={disableV2} />
        <div style={{ marginBottom: 14 }}>
          <div className="t2-page-eyebrow">
            <Factory className="w-3 h-3" /> Data Lab · Suppliers
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: "var(--t2-white)", margin: "4px 0 3px" }}>Suppliers</h1>
          <p className="t2-mono" style={{ fontSize: 11.5, color: "var(--t2-grey3)" }}>
            Directory + rebate / discount ledger. Rebates % feed the quote-builder margin calculator.
          </p>
        </div>

        <KpiBand testid="suppliers-kpis">
          <KpiTile label="Suppliers"     value={insights?.total ?? "…"}                              icon={Factory}   testid="kpi-suppliers-total" />
          <KpiTile label="Enabled"       value={insights?.enabled ?? "…"}                            icon={ToggleRight} tone="teal" testid="kpi-suppliers-enabled" />
          <KpiTile label="With rebate"   value={insights?.with_rebate ?? "…"}                        icon={Percent}   tone="orange" testid="kpi-suppliers-rebate-count" />
          <KpiTile label="Avg rebate"    value={`${(insights?.avg_rebate ?? 0)}%`}                   icon={Percent}   tone="brass"  testid="kpi-suppliers-avg-rebate" />
          <KpiTile label="Avg discount"  value={`${(insights?.avg_discount ?? 0)}%`}                 icon={Percent}   tone="brass"  testid="kpi-suppliers-avg-discount" />
          <KpiTile label="Top category"  value={asText(insights?.categories?.[0]?.category || "—")}  icon={Trophy}    tone="violet" testid="kpi-suppliers-top-cat" />
        </KpiBand>

        <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 14, marginBottom: 14 }}>
          <T2Panel icon={Trophy} title="Top rebate earners" testid="suppliers-top-rebate">
            {(insights?.top_rebate || []).length === 0 ? (
              <div className="t2-mono" style={{ fontSize: 11, color: "var(--t2-grey4)" }}>No suppliers with a rebate configured.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {insights.top_rebate.map((s, i) => (
                  <Link key={s.id} to={`/lab/suppliers/${s.id}`}
                    style={{
                      display: "grid", gridTemplateColumns: "auto 1fr auto auto", gap: 8, alignItems: "center",
                      padding: "8px 10px", borderRadius: 8,
                      background: "var(--t2-surface2)", border: "1px solid var(--t2-border)",
                      textDecoration: "none",
                    }}
                    data-testid={`supplier-top-rebate-${i}`}
                  >
                    <div className="t2-mono" style={{ color: "var(--t2-brass)", fontSize: 12, fontWeight: 800 }}>#{i + 1}</div>
                    <div style={{ color: "var(--t2-white)", fontWeight: 700, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</div>
                    <div className="t2-mono" style={{ color: "var(--t2-teal)", fontWeight: 700, fontSize: 12 }}>{PCT(s.rebate)}</div>
                    <div className="t2-mono" style={{ color: "var(--t2-brass)", fontWeight: 600, fontSize: 11 }}>disc {PCT(s.discount)}</div>
                  </Link>
                ))}
              </div>
            )}
          </T2Panel>
          <T2Panel icon={Factory} title="Filter by category" testid="suppliers-facet-cat">
            <FacetChips
              testid="suppliers-facet-cat-chips"
              options={(insights?.categories || []).map((s) => ({ value: s.category, count: s.count }))}
              selected={cats}
              onChange={setCats}
            />
          </T2Panel>
        </div>

        <LabListTable
          collection="suppliers"
          columns={COLUMNS}
          defaultSort="SupplierName"
          defaultOrder="asc"
          detailPath={(id) => `/lab/suppliers/${id}`}
          searchPlaceholder="Search supplier name, account #, contact, city, postcode, category…"
          testidPrefix="lab-supplier"
          alphaField="SupplierName"
          extraSearch={searchAppend}
        />
      </div>
    </Layout>
  );
}
